import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../Icon/Icon';
import { STATUS_COLORS } from '../../theme/tokens';
import { Root, Trigger, PanelRoot, Item, ItemLabel } from './ActionMenu.styles';

export type ActionMenuItem = {
  /** Visible copy; the English source doubles as the i18n key. */
  label: string;
  icon?: IconName;
  /**
   * Paints the row in the negative status colour. For actions that remove or take
   * something down (delete a draft, unpublish an article), not merely secondary ones.
   */
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

export type ActionMenuProps = {
  items: ActionMenuItem[];
  /** Accessible name of the kebab trigger. */
  label?: string;
  /** Which edge of the trigger the panel lines up with. */
  align?: 'start' | 'end';
  disabled?: boolean;
  className?: string;
  /**
   * Fixed chrome at the top / bottom of the screen (a pinned header, a bottom nav bar) that counts as out of
   * view: the panel opens upward when dropping down would end past the bottom edge less this inset and
   * there is more room above. A function is called each time the panel opens, for bounds that depend on
   * layout at that moment (a menu inside a scrolling dialog).
   */
  viewportInsets?: MenuInsets | (() => MenuInsets);
  /** Called with `true` when the panel opens and `false` when it closes, however it was dismissed. */
  onOpenChange?: (open: boolean) => void;
};

export type MenuInsets = { top?: number; bottom?: number };

/** The gap between the trigger and the panel, as the panel styles place it. */
const PANEL_GAP_PX = 6;

/**
 * Kebab trigger + a dropped list of actions — the "everything else" companion to
 * a row's or a page's primary button, so destructive and rare actions stay
 * reachable without spending a button on each.
 *
 * The panel is absolutely positioned inside the trigger's own box rather than
 * portalled, so it tracks the trigger through scrolling and inherits the
 * surrounding stacking context. That trades away the ability to escape an
 * `overflow: hidden` ancestor; the area's Panel does not clip, and a menu that
 * detaches from its trigger mid-scroll is the worse failure.
 *
 * Dismissal covers all three exits a menu needs: Escape, a click anywhere outside,
 * and choosing an item. Focus returns to the trigger afterwards so keyboard users
 * do not get dropped at the top of the document.
 *
 * The panel always opens in view: it is measured before it paints, and flips above
 * the trigger when dropping down would run past the bottom of the screen (or into
 * `viewportInsets`) and there is more room above. Moving focus into it never
 * scrolls the page.
 */
export const ActionMenu = ({
  items,
  label = 'More actions',
  align = 'end',
  disabled = false,
  className,
  viewportInsets,
  onOpenChange,
}: ActionMenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpenState] = React.useState(false);
  const [side, setSide] = React.useState<'below' | 'above'>('below');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  // The latest insets, read when the panel opens (a function form is evaluated then, not at render).
  const insetsRef = React.useRef(viewportInsets);
  React.useLayoutEffect(() => {
    insetsRef.current = viewportInsets;
  }, [viewportInsets]);

  // The latest callback, so `close` stays stable for the dismissal listeners below.
  const onOpenChangeRef = React.useRef(onOpenChange);
  React.useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const setOpen = React.useCallback((next: boolean): void => {
    setOpenState(next);
    onOpenChangeRef.current?.(next);
  }, []);

  const close = React.useCallback(
    (refocus: boolean): void => {
      setOpen(false);
      if (refocus) triggerRef.current?.focus({ preventScroll: true });
    },
    [setOpen],
  );

  // Pick the side before the panel paints: below by default, above when below would leave the screen and
  // above has more room. A state update in a layout effect re-renders before paint, so it never flickers.
  React.useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;
    const given = insetsRef.current;
    const insets = typeof given === 'function' ? given() : given;
    const anchor = trigger.getBoundingClientRect();
    const height = panel.getBoundingClientRect().height;
    const roomBelow = window.innerHeight - (insets?.bottom ?? 0) - (anchor.bottom + PANEL_GAP_PX);
    const roomAbove = anchor.top - PANEL_GAP_PX - (insets?.top ?? 0);
    setSide(roomBelow < height && roomAbove > roomBelow ? 'above' : 'below');
  }, [open]);

  // Outside click + Escape. `mousedown` rather than `click`: a click that starts
  // outside and ends inside (a drag-release) should still count as "left the menu",
  // and closing on mousedown also beats the browser to any focus change.
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) close(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Hand focus to the first enabled item on open, so the menu is operable from the
  // keyboard the moment it appears.
  React.useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    first?.focus({ preventScroll: true });
  }, [open]);

  const onItemKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    // Wraps at both ends: from the last item down lands on the first, and Up from
    // the first (or from nowhere, when at === -1) lands on the last.
    const next = (at + step + buttons.length) % buttons.length;
    buttons[next].focus();
  };

  return (
    <Root ref={rootRef} className={className}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={t(label)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <Icon name="more" size={18} />
      </Trigger>

      {open ? (
        <PanelRoot ref={panelRef} role="menu" $align={align} $side={side} onKeyDown={onItemKeyDown}>
          {items.map((item) => (
            <Item
              key={item.label}
              type="button"
              role="menuitem"
              $destructive={Boolean(item.destructive)}
              disabled={item.disabled}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.icon ? (
                <Icon name={item.icon} size={16} color={item.destructive ? STATUS_COLORS.negative : undefined} />
              ) : null}
              <ItemLabel text={item.label} tag="span" />
            </Item>
          ))}
        </PanelRoot>
      ) : null}
    </Root>
  );
};

export default ActionMenu;
